/**
 * Shared props for founding-admin onboarding Amplitude events (RR-131 / RR-132).
 *
 * Kept outside the welcome-v2 module so post-chat surfaces (homepage demo
 * recognition, redeem offer) can attach the same `variant` / `intentSegment` /
 * `daysSinceSignup` without importing a module.
 */

/**
 * Primary self-reported intent for the founding admin. Matches the goal chip
 * ids in welcome-v2 `GOAL_OPTIONS`; `none` means no chip selected.
 */
export type IntentSegment = 'engagement' | 'culture_change' | 'awards' | 'gifting' | 'exploring' | 'none';

// Bizy-led onboarding variant (gated behind `plg-bizy-led-onboarding`); the
// legacy flow is `control`.
export const ONBOARDING_VARIANT = 'bizy_onboarding';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** sessionStorage key so intent survives welcome-v2 unmount → homepage demo. */
export const ONBOARDING_INTENT_STORAGE_KEY = 'onboardingAdminIntentSegment';

const INTENT_SEGMENTS = new Set<IntentSegment>(['engagement', 'culture_change', 'awards', 'gifting', 'exploring', 'none']);

export const computeDaysSinceSignup = (createdAt?: string | null): number => {
	if (!createdAt) return 0;
	const created = new Date(createdAt).getTime();
	if (Number.isNaN(created)) return 0;
	return Math.max(0, Math.floor((Date.now() - created) / MS_PER_DAY));
};

export const persistIntentSegment = (primary: IntentSegment): void => {
	try {
		sessionStorage.setItem(ONBOARDING_INTENT_STORAGE_KEY, primary);
	} catch {
		// Private mode / quota — events still fire with in-memory or `none`.
	}
};

export const readIntentSegment = (): IntentSegment => {
	try {
		const stored = sessionStorage.getItem(ONBOARDING_INTENT_STORAGE_KEY);
		if (stored && INTENT_SEGMENTS.has(stored as IntentSegment)) {
			return stored as IntentSegment;
		}
	} catch {
		// ignore
	}
	return 'none';
};

export type OnboardingAdminSharedProps = {
	variant: typeof ONBOARDING_VARIANT;
	daysSinceSignup: number;
	intentSegment: IntentSegment;
};

export const buildOnboardingAdminSharedProps = (createdAt?: string | null): OnboardingAdminSharedProps => ({
	variant: ONBOARDING_VARIANT,
	daysSinceSignup: computeDaysSinceSignup(createdAt),
	intentSegment: readIntentSegment(),
});
